import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DollarSign, ShoppingCart, TrendingUp, Package } from 'lucide-react';
import { BusinessKPIs, ChartTheme } from './types';
import { KPICard, KPICardGrid } from './KPICard';
import { getBaseChartOptions, formatCurrency, formatDate } from './chartUtils';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { getCountryGeoForSales, normalizeCountryCode, type CountryGeo } from '@/services/country/country-geocode-service';

interface BusinessSectionProps {
    data: BusinessKPIs;
    theme: ChartTheme;
}

export const BusinessSection: React.FC<BusinessSectionProps> = ({ data, theme }) => {
    const { t } = useTranslation();
    const baseOptions = getBaseChartOptions(theme);

    // Géocodage dynamique des pays (API REST Countries)
    const [countryGeoMap, setCountryGeoMap] = useState<Map<string, CountryGeo>>(new Map());
    const [geoLoading, setGeoLoading] = useState(true);
    useEffect(() => {
        if (!data.salesByCountry.length) {
            setCountryGeoMap(new Map());
            setGeoLoading(false);
            return;
        }
        setGeoLoading(true);
        getCountryGeoForSales(data.salesByCountry)
            .then((map) => {
                setCountryGeoMap(map);
                setGeoLoading(false);
            })
            .catch(() => setGeoLoading(false));
    }, [data.salesByCountry]);

    /** Nom du pays dans la langue de l’app (i18n), sinon API, sinon code ISO. */
    const getCountryDisplayName = (code: string) => {
        const norm = normalizeCountryCode(code);
        const i18nKey = `admin.dashboard.analytics.business.countryNames.${norm}`;
        const translated = t(i18nKey);
        if (translated && translated !== i18nKey) return translated;
        return countryGeoMap.get(norm)?.name ?? code;
    };

    const [geoView, setGeoView] = useState<'country' | 'city'>('country');

    // Line/Area Chart - Évolution du CA
    const revenueChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'areaspline',
            height: 300,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.business.revenueEvolution'),
        },
        xAxis: {
            ...baseOptions.xAxis,
            categories: data.revenueByDay.map(d => formatDate(d.date)),
        },
        yAxis: {
            ...baseOptions.yAxis,
            title: { text: 'CA (€)', style: { color: theme.textColor } },
        },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function () {
                return `<b>${this.x}</b><br/>CA: ${formatCurrency(this.y as number)}`;
            },
        },
        series: [{
            name: 'CA',
            type: 'areaspline',
            data: data.revenueByDay.map(d => d.amount),
            color: theme.primaryColor,
            fillColor: {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [
                    [0, Highcharts.color(theme.primaryColor).setOpacity(0.4).get('rgba') as string],
                    [1, Highcharts.color(theme.primaryColor).setOpacity(0).get('rgba') as string],
                ],
            },
            marker: { enabled: false },
        }],
    };

    // Column Chart - Nombre de ventes par période
    const salesChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'column',
            height: 300,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.business.salesVolume'),
        },
        xAxis: {
            ...baseOptions.xAxis,
            categories: data.salesByDay.map(d => formatDate(d.date)),
        },
        yAxis: {
            ...baseOptions.yAxis,
            title: { text: 'Ventes', style: { color: theme.textColor } },
            allowDecimals: false,
        },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function () {
                return `<b>${this.x}</b><br/>Ventes: ${this.y}`;
            },
        },
        plotOptions: {
            column: {
                borderRadius: 4,
                borderWidth: 0,
            },
        },
        series: [{
            name: 'Ventes',
            type: 'column',
            data: data.salesByDay.map(d => d.count),
            color: theme.secondaryColor,
        }],
    };

    return (
        <section className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-primary-10 rounded-lg">
                    <DollarSign className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">{t('admin.dashboard.analytics.business.title')}</h2>
            </div>

            {/* KPI Cards */}
            <KPICardGrid columns={4}>
                <KPICard
                    title={t('admin.dashboard.analytics.business.kpis.revenue')}
                    value={data.totalRevenue}
                    format="currency"
                    icon={<DollarSign className="h-5 w-5 text-primary" />}
                    color="primary"
                    tooltip={t('admin.dashboard.analytics.business.kpis.revenueTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.business.kpis.salesCount')}
                    value={data.salesCount}
                    format="number"
                    icon={<ShoppingCart className="h-5 w-5 text-secondary" />}
                    color="primary"
                    tooltip={t('admin.dashboard.analytics.business.kpis.salesCountTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.business.kpis.averageCart')}
                    value={data.averageCart}
                    format="currency"
                    icon={<TrendingUp className="h-5 w-5 text-success" />}
                    color="success"
                    tooltip={t('admin.dashboard.analytics.business.kpis.averageCartTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.business.kpis.productsSold')}
                    value={data.productsSold}
                    format="number"
                    icon={<Package className="h-5 w-5 text-info" />}
                    color="info"
                    tooltip={t('admin.dashboard.analytics.business.kpis.productsSoldTooltip')}
                />
            </KPICardGrid>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-surface border border-border rounded-xl p-4">
                    <HighchartsReact highcharts={Highcharts} options={revenueChartOptions} />
                </div>
                <div className="bg-surface border border-border rounded-xl p-4">
                    <HighchartsReact highcharts={Highcharts} options={salesChartOptions} />
                </div>
            </div>

            {/* Geographic Distribution */}
            <div className="bg-surface border border-border rounded-xl p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-info" />
                        <h3 className="text-lg font-semibold text-foreground">{t('admin.dashboard.analytics.business.geographicDistribution')}</h3>
                    </div>
                    <div className="flex rounded-lg border border-border p-0.5 bg-background-tertiary">
                        <button
                            type="button"
                            onClick={() => setGeoView('country')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${geoView === 'country' ? 'bg-primary text-white' : 'text-foreground-secondary hover:text-foreground'}`}
                        >
                            {t('admin.dashboard.analytics.business.byCountry')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setGeoView('city')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${geoView === 'city' ? 'bg-primary text-white' : 'text-foreground-secondary hover:text-foreground'}`}
                        >
                            {t('admin.dashboard.analytics.business.byCity')}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Map Container */}
                    <div className="lg:col-span-2 relative">
                        {geoLoading && (
                            <div className="absolute inset-0 z-[1000] flex items-center justify-center rounded-xl bg-background-tertiary/80">
                                <span className="text-sm text-foreground-secondary">{t('common.loading')}</span>
                            </div>
                        )}
                        <div className="h-[400px] rounded-xl overflow-hidden border border-border bg-background-tertiary">
                            <MapContainer
                                key="sales-map"
                                center={[46, 2] as any}
                                zoom={4}
                                style={{ height: '100%', width: '100%' }}
                                scrollWheelZoom={true}
                                dragging={true}
                                doubleClickZoom={true}
                                touchZoom={true}
                                zoomControl={true}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                {!geoLoading && data.salesByCountry.map((c, idx) => {
                                    const geo = countryGeoMap.get(normalizeCountryCode(c.country));
                                    if (!geo?.latlng) return null;

                                    const maxCount = Math.max(...data.salesByCountry.map(ct => ct.count), 1);
                                    const radius = 8 + (c.count / maxCount) * 20;

                                    return (
                                        <CircleMarker
                                            key={`${c.country}-${idx}`}
                                            center={geo.latlng}
                                            radius={radius}
                                            pathOptions={{
                                                fillColor: theme.primaryColor,
                                                fillOpacity: 0.6,
                                                color: theme.primaryColor,
                                                weight: 2,
                                                stroke: true,
                                            }}
                                        >
                                            <LeafletTooltip direction="top" offset={[0, -10]} opacity={1}>
                                                <div className="p-1 px-2">
                                                    <p className="font-bold text-sm mb-0.5">{getCountryDisplayName(c.country)}</p>
                                                    <p className="text-xs text-foreground-secondary">{c.count} {t('admin.dashboard.analytics.common.orders')} • {formatCurrency(c.revenue)}</p>
                                                </div>
                                            </LeafletTooltip>
                                        </CircleMarker>
                                    );
                                })}
                            </MapContainer>
                        </div>
                    </div>

                    {/* Table Details: Pays ou Villes */}
                    <div className="flex flex-col justify-center">
                        <div className="space-y-4">
                            <h4 className="text-sm font-medium text-foreground-secondary uppercase tracking-wider">
                                {geoView === 'country' ? t('admin.dashboard.analytics.business.topDestinations') : t('admin.dashboard.analytics.business.topCities')}
                            </h4>
                            {geoView === 'country' && data.salesByCountry.slice(0, 5).map((item) => (
                                <div key={item.country} className="flex items-center justify-between p-3 rounded-lg bg-background-tertiary/50 border border-border/50">
                                    <div className="flex items-center gap-3">
                                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-surface text-xs font-bold border border-border">
                                            {item.country}
                                        </span>
                                        <div>
                                            <div className="text-sm font-bold text-foreground">
                                                {getCountryDisplayName(item.country)}
                                            </div>
                                            <div className="text-xs text-foreground-secondary">{item.count} {t('admin.dashboard.analytics.common.orders')}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-bold text-primary">{formatCurrency(item.revenue)}</div>
                                        <div className="text-[10px] text-foreground-secondary">
                                            {data.totalRevenue > 0 ? ((item.revenue / data.totalRevenue) * 100).toFixed(1) : 0}% {t('admin.dashboard.analytics.common.total')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {geoView === 'city' && (data.salesByCity.length > 0 ? data.salesByCity.slice(0, 5).map((item) => (
                                <div key={`${item.city}|${item.country}`} className="flex items-center justify-between p-3 rounded-lg bg-background-tertiary/50 border border-border/50">
                                    <div className="flex items-center gap-3">
                                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-surface text-xs font-bold border border-border">
                                            {item.country}
                                        </span>
                                        <div>
                                            <div className="text-sm font-bold text-foreground">
                                                {item.city}
                                            </div>
                                            <div className="text-xs text-foreground-secondary">{item.count} {t('admin.dashboard.analytics.common.orders')} • {getCountryDisplayName(item.country)}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-bold text-primary">{formatCurrency(item.revenue)}</div>
                                        <div className="text-[10px] text-foreground-secondary">
                                            {data.totalRevenue > 0 ? ((item.revenue / data.totalRevenue) * 100).toFixed(1) : 0}% {t('admin.dashboard.analytics.common.total')}
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <p className="text-sm text-foreground-secondary py-4">{t('admin.dashboard.analytics.business.noCityData')}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
